<template>
  <div id="app" class="page">
    <router-view></router-view>
    <YunTiUinLoginTip :show.sync="showTips" />
  </div>
</template>

<script>
import eventProxy from '@/scripts/tools/eventProxy';
import user from '@/scripts/helper/user';
import keyBox from '@/scripts/helper/keyBox';
import dao from '@/scripts/helper/dao';
import Route from '@/scripts/helper/routeJump';
import storage from '@/scripts/tools/storage';
import oauth from '@/scripts/helper/oauth';
import { tips, getUrlQuery } from '@/scripts/tools/utils';
import tabMessage from '@/scripts/common/tabMessage';
// eslint-disable-next-line no-unused-vars
import reporter from '@/scripts/common/reporter';

import YunTiUinLoginTip from '@/components/dialog/others/YunTiUinLoginTip.vue';

export default {
  name: 'COSBrowser-web',
  components: { YunTiUinLoginTip },
  computed: {
    userInfo() {
      return this.$store.userInfo;
    },
  },
  data() {
    return {
      showTips: false,
    };
  },
  watch: {
    userInfo() {
      // 用户信息改变时保存至本地
      this.$mutations.saveStore();
    },
    $route(val, old) {
      // console.log('$route -> val, old', val, old);
      if (!user.isLogin() && !['login', 'history', 'editor', 'file'].includes(val.name)) {
        Route.RouteJumpLogin(val);
      } else if (val.query.from && ['editor', 'file'].includes(val.name)) {
        if (user.isLogin()) {
          const { uin, ownerUin } = val.query;
          const userInfo = storage.get('userInfo', 'local');
          if (uin === userInfo.Uin && ownerUin === userInfo.OwnerUin) {
            Route.RouteJumpEdit(val);
          } else {
            Route.RouteJumpOauth(val);
          }
        } else {
          Route.RouteJumpOauth(val);
        }
      } else if (!val.query.from && ['editor', 'file'].includes(val.name)) {
        if (!user.isLogin()) {
          Route.RouteJumpLogin(val);
        }
      } else if (
        user.isLogin()
        && val.name === 'login'
        && old.name !== 'login'
        && !storage.get('set_path', 'session')
      ) {
        // 如果已登录且要跳转到登陆页面，则强制不允许跳转
        this.computeCrumbs();
      } else if (val.name === 'web' && old.name !== 'login') {
        // 如果路由跳转到 web,则计算面包屑导航进入正确的页面
        this.computeCrumbs();
      }
    },
  },
  mounted() {
    this.handleQcloudLogin();

    // 监听应用窗口大小改变
    window.onresize = e => {
      const { innerHeight: height, innerWidth: width } = e.target;
      const size = { height, width };
      eventProxy.emit('winResize', size);
    };

    // 监听登录事件切换路由
    eventProxy.on('after-login', async () => {
      await this.initUserInfo();
      if (this.isYunTiUinSecretKeyLogin()) {
        this.$router.replace({ name: 'login' }).catch(() => { });
        user.logout();
        tips.closeTips();
        setTimeout(() => {
          // 弹窗提示云梯子账号不能用永久密钥登录
          this.showTips = true;
        }, 100);
        return;
      }
      if (this.$store.isLogin && this.$route.name === 'login') {
        const setUrl = storage.get('set_path', 'session');
        const loginUrl = storage.get('login_path', 'session');
        storage.del('set_path', 'session');
        storage.del('login_path', 'session');
        if (setUrl) {
          if (setUrl.name === 'file') {
            if (setUrl.query.key) {
              const editKey = setUrl.query.key;
              const editPath = decodeURIComponent(editKey);
              setUrl.query.path = editPath;
            }
            Route.RouteJump(setUrl);
            // 注意此延时时间要稍长一点
            setTimeout(() => {
              eventProxy.emit('command.edit', setUrl.query);
            }, 4000);
          } else if (setUrl.name === 'editor') {
            Route.RouteJump(setUrl);
          }
        } else if (loginUrl) {
          this.$router.replace({ path: loginUrl }).catch(err => err);
        } else {
          this.computeCrumbs();
        }
      }
    });
    //  监听登出事件切换路由
    eventProxy.on('after-logout', () => {
      this.$mutations.initStoreAfterLogout();
      this.$router.replace({ name: 'login' }).catch(() => { });
    });
  },
  methods: {
    // 全局方法-获取腾讯云的登录信息
    async handleQcloudLogin() {
      const oauthInfo = storage.get('oauth_cache');
      setTimeout(() => {
        storage.del('oauth_cache');
      }, 100);
      if (!oauthInfo) return;
      const queryInfo = getUrlQuery();
      const clientId = oauthInfo?.clientId || queryInfo?.clientId;
      const clientCode = oauthInfo?.clientCode || queryInfo?.clientCode;
      if (clientId && clientCode) {
        tips.loading(this.$t('core.loading'));
        oauth.clientId = clientId;
        oauth.clientCode = clientCode;
        if (queryInfo.authCode) oauth.authCode = queryInfo.authCode;
        const credential = await oauth.getLoginInfo();
        if (!Object.keys(credential).length) {
          // TODO: 查看
          tips.error(this.$t('core.auth_failed'));
          return;
        }
        user.setCredential(credential);
        // 发送logout的消息
        tabMessage.sendMessage('logoutMessage');
        user.login({ keyType: 'QcloudAccount' });
      }
    },
    async computeCrumbs() {
      const { crumbs } = this.$store;
      if (crumbs.length === 0) {
        // 第一次加载检查是否有需要进入路径
        const { AccessPath, Region: region } = await user.getCredential();
        if (AccessPath) {
          const bucket = AccessPath.substr(0, AccessPath.indexOf('/'));
          let path = AccessPath.substr(AccessPath.indexOf('/'));
          path = (path || '').replace(/^\//, '');

          const query = { bucket, region, path };
          if (!query.path) delete query.path;
          this.$router.replace({ name: 'file', query }).catch(() => { });
        } else if (this.$route.query.r_path) {
          // 编辑器模式下的跳转
          const editorPath = decodeURIComponent(this.$route.query.r_path);
          this.$router.replace({ path: editorPath }).catch(() => { });
        } else {
          // 正常登录下的跳转
          this.$router.replace({ name: 'bucket' }).catch(() => { });
        }
      } else if (crumbs.length === 1) {
        this.$router.replace({ name: 'bucket' }).catch(() => { });
      } else {
        const { name: bucket, region } = this.$store.crumbs[1];
        const path = this.$store.crumbs.reduce((res, item, index) => {
          if (index > 1) {
            res += `${item.name}/`;
          }
          return res;
        }, '');
        const query = { bucket, region, path };
        if (!query.path) delete query.path;
        this.$router.replace({ name: 'file', query }).catch(() => { });
      }
    },
    /**
     * 判断是否是云梯子账号使用永久密钥进行登录
     */
    isYunTiUinSecretKeyLogin() {
      const keyType = keyBox.getKeyType();
      if (keyType === 'SecretKey' && this.$store.userInfo.IsYunTiUin) {
        return true;
      }
      return false;
    },
    async initUserInfo() {
      this.$store.isLogin = true;
      if (Object.keys(this.$store.userInfo).length > 0) return;
      const res = await dao.getUserInfo();
      res.AppId = String(res.AppId);
      this.$store.userInfo = res;
    },
  },
};
</script>
